// Parcours de découverte — wizard multi-étapes.
// Sauvegardé en localStorage, rattaché au compte à l'inscription/connexion.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { store } from "@/lib/storage";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const NEEDS = [
  { id: "pause", label: "Faire une pause" },
  { id: "agitation", label: "Apaiser l'agitation" },
  { id: "tensions", label: "Relâcher les tensions" },
  { id: "decompression", label: "Décompresser après le travail" },
  { id: "sport", label: "Revenir au calme après le sport" },
  { id: "soiree", label: "Redescendre après une soirée" },
  { id: "sommeil", label: "Préparer le sommeil" },
  { id: "sons", label: "Profiter des sons et des images" },
];

const FEELINGS = [
  { id: "pensees", label: "Pensées qui tournent" },
  { id: "corps", label: "Corps tendu" },
  { id: "stim", label: "Environnement trop stimulant" },
  { id: "ralentir", label: "Difficulté à ralentir" },
  { id: "presence", label: "Envie d'une présence douce" },
  { id: "silence", label: "Besoin de silence ou d'ambiance sans parole" },
];

const BABY_MOMENTS = [
  { id: "calme", label: "Temps calme ensemble" },
  { id: "familier", label: "Ambiance familière" },
  { id: "endormissement", label: "Accompagnement de l'endormissement" },
];

const UNIVERSES = [
  { id: "pluie", label: "Pluie" },
  { id: "ocean", label: "Océan" },
  { id: "foret", label: "Forêt" },
  { id: "musique", label: "Musique enveloppante" },
  { id: "asmr", label: "ASMR délicat" },
  { id: "voix", label: "Voix douce" },
  { id: "sans_voix", label: "Sans voix" },
  { id: "decouvrir", label: "Je découvre" },
];

const AVOID = [
  { id: "chuchotements", label: "Chuchotements" },
  { id: "repetitif", label: "Sons répétitifs" },
];

const VISUALS = [
  { id: "nature", label: "Nature et paysages" },
  { id: "abstrait", label: "Images abstraites douces" },
  { id: "sobre", label: "Écran sobre ou sombre" },
  { id: "peu_importe", label: "Peu importe" },
];

const DURATIONS = [5, 10, 20];
const MOMENTS = [
  { id: "matin", label: "Matin" },
  { id: "journee", label: "Journée" },
  { id: "soir", label: "Soir" },
  { id: "envies", label: "Selon mes envies" },
];

const SPACE_NAMES = ["Ma bulle", "Mon refuge", "Mon cocon"];

const emptyProfile = () => ({
  displayName: "",
  age: null,
  audience: "adult", // adult | baby | mixed
  babyAgeMonths: null,
  needs: [],
  priorityNeed: null,
  feelings: [],
  babyMoments: [],
  universes: [],
  avoid: [],
  visuals: null,
  duration: 5,
  moment: null,
  spaceName: "",
  completed: false,
});

export default function Discovery() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(() => store.getProfile() || emptyProfile());

  const isBabyOnly = profile.audience === "baby";
  const isMixed = profile.audience === "mixed";
  const hasAdult = profile.audience === "adult" || isMixed;

  // Steps calculés selon audience
  const steps = useMemo(() => {
    const base = ["name", "age", "audience", "needs"];
    if (hasAdult) base.push("feelings");
    if (isBabyOnly || isMixed) base.push("baby");
    base.push("universe", "visuals", "rhythm", "spaceName", "summary");
    return base;
  }, [hasAdult, isBabyOnly, isMixed]);

  const currentKey = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  function update(patch) {
    const next = { ...profile, ...patch };
    setProfile(next);
    store.setProfile(next);
  }

  function nextStep() {
    if (step + 1 < steps.length) setStep(step + 1);
  }
  function prevStep() {
    if (step > 0) setStep(step - 1);
  }

  async function finish() {
    const final = { ...profile, completed: true };
    setProfile(final);
    store.setProfile(final);
    store.setDiscoveryPrompt(true);
    if (user) {
      try { await api.put("/profile", { data: final }); } catch {}
    }
    nav("/");
  }

  function skipAll() {
    store.setDiscoveryPrompt(true);
    nav("/");
  }

  return (
    <div className="animate-fade-in pt-6 pb-8" data-testid="discovery-page">
      <div className="flex items-center justify-between mb-4">
        {step > 0 ? (
          <button data-testid="disc-back" onClick={prevStep} className="flex items-center gap-1 text-[color:var(--ivoire-muted)] text-sm">
            <ChevronLeft size={16} /> Retour
          </button>
        ) : (
          <button data-testid="disc-skip-all" onClick={skipAll} className="text-[color:var(--ivoire-muted)] text-xs underline underline-offset-4">
            Passer la découverte
          </button>
        )}
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">
          {step + 1} / {steps.length}
        </p>
      </div>
      <div className="h-1 bg-[color:var(--bg-surface)] rounded-full overflow-hidden">
        <div className="h-full bg-[color:var(--sauge)] transition-all" style={{ width: `${progress}%` }} />
      </div>

      <div className="mt-8">
        {currentKey === "name" && (
          <StepBlock title="Comment souhaitez-vous qu'on vous appelle ?" hint="Prénom ou surnom.">
            <input
              data-testid="disc-name"
              value={profile.displayName}
              onChange={(e) => update({ displayName: e.target.value })}
              className="w-full h-12 px-4 rounded-xl bg-[#142235]/60 border border-[color:var(--border)] text-[color:var(--ivoire)]"
              placeholder="Votre prénom"
              autoFocus
            />
          </StepBlock>
        )}

        {currentKey === "age" && (
          <StepBlock title="Quel âge avez-vous ?" hint="Facultatif — utilisé uniquement pour affiner les suggestions.">
            <input
              data-testid="disc-age"
              type="number" min="1" max="120"
              value={profile.age || ""}
              onChange={(e) => update({ age: e.target.value ? parseInt(e.target.value, 10) : null })}
              className="w-full h-12 px-4 rounded-xl bg-[#142235]/60 border border-[color:var(--border)] text-[color:var(--ivoire)]"
              placeholder="Âge en années"
            />
            <button data-testid="disc-skip" onClick={nextStep} className="mt-3 text-xs text-[color:var(--ivoire-muted)] underline underline-offset-4">
              Passer cette question
            </button>
          </StepBlock>
        )}

        {currentKey === "audience" && (
          <StepBlock title="Pour qui cherchez-vous ces moments de calme ?">
            <RadioList
              options={[
                { id: "adult", label: "Pour moi" },
                { id: "baby", label: "Pour mon bébé" },
                { id: "mixed", label: "Pour moi et mon bébé" },
              ]}
              value={profile.audience}
              onChange={(v) => update({ audience: v })}
              testidPrefix="disc-aud"
            />
          </StepBlock>
        )}

        {currentKey === "needs" && (
          <StepBlock title="De quoi avez-vous le plus besoin en ce moment ?" hint="Plusieurs choix possibles.">
            <MultiList
              options={NEEDS}
              value={profile.needs}
              onChange={(v) => update({ needs: v, priorityNeed: v[0] || null })}
              testidPrefix="disc-need"
            />
            {profile.needs.length > 1 && (
              <div className="mt-6">
                <p className="text-xs text-[color:var(--ivoire-muted)] mb-2">Choisissez votre priorité :</p>
                <RadioList
                  options={NEEDS.filter((n) => profile.needs.includes(n.id))}
                  value={profile.priorityNeed}
                  onChange={(v) => update({ priorityNeed: v })}
                  testidPrefix="disc-prio"
                />
              </div>
            )}
          </StepBlock>
        )}

        {currentKey === "feelings" && (
          <StepBlock title="Dans ces moments-là, qu'est-ce qui vous ressemble le plus ?" hint="Plusieurs choix possibles.">
            <MultiList
              options={FEELINGS}
              value={profile.feelings}
              onChange={(v) => update({ feelings: v })}
              testidPrefix="disc-feel"
            />
            <button data-testid="disc-skip" onClick={nextStep} className="mt-3 text-xs text-[color:var(--ivoire-muted)] underline underline-offset-4">
              Passer cette question
            </button>
          </StepBlock>
        )}

        {currentKey === "baby" && (
          <StepBlock title="Quels moments cherchez-vous pour bébé ?">
            <MultiList
              options={BABY_MOMENTS}
              value={profile.babyMoments}
              onChange={(v) => update({ babyMoments: v })}
              testidPrefix="disc-baby"
            />
            <div className="mt-6">
              <p className="text-xs text-[color:var(--ivoire-muted)] mb-2">Âge du bébé en mois (facultatif) :</p>
              <input
                data-testid="disc-baby-age"
                type="number" min="0" max="36"
                value={profile.babyAgeMonths || ""}
                onChange={(e) => update({ babyAgeMonths: e.target.value ? parseInt(e.target.value, 10) : null })}
                className="w-full h-12 px-4 rounded-xl bg-[#142235]/60 border border-[color:var(--border)] text-[color:var(--ivoire)]"
                placeholder="Âge en mois"
              />
            </div>
          </StepBlock>
        )}

        {currentKey === "universe" && (
          <StepBlock title="Quel univers vous fait du bien ?" hint="Plusieurs choix possibles.">
            <MultiList
              options={UNIVERSES}
              value={profile.universes}
              onChange={(v) => update({ universes: v })}
              testidPrefix="disc-univ"
            />
            <div className="mt-6">
              <p className="text-xs text-[color:var(--ivoire-muted)] mb-2">Sons à éviter :</p>
              <MultiList
                options={AVOID}
                value={profile.avoid}
                onChange={(v) => update({ avoid: v })}
                testidPrefix="disc-avoid"
              />
            </div>
          </StepBlock>
        )}

        {currentKey === "visuals" && (
          <StepBlock title="Et côté images ?">
            <RadioList
              options={VISUALS}
              value={profile.visuals}
              onChange={(v) => update({ visuals: v })}
              testidPrefix="disc-vis"
            />
          </StepBlock>
        )}

        {currentKey === "rhythm" && (
          <StepBlock title="Quel rythme vous conviendrait ?">
            <div className="flex gap-2 mb-6">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  data-testid={`disc-dur-${d}`}
                  onClick={() => update({ duration: d })}
                  className={`flex-1 h-12 rounded-xl border font-mono text-sm transition ${
                    profile.duration === d
                      ? "bg-[color:var(--sauge)] text-[color:var(--bg-deep)] border-[color:var(--sauge)]"
                      : "border-[color:var(--border)] bg-[#142235]/60 text-[color:var(--ivoire)]"
                  }`}
                >{d} min</button>
              ))}
            </div>
            <RadioList
              options={MOMENTS}
              value={profile.moment}
              onChange={(v) => update({ moment: v })}
              testidPrefix="disc-mom"
            />
          </StepBlock>
        )}

        {currentKey === "spaceName" && (
          <StepBlock title="Donnez un petit nom à votre espace" hint="Facultatif.">
            <div className="grid grid-cols-3 gap-2 mb-3">
              {SPACE_NAMES.map((n) => (
                <button
                  key={n}
                  data-testid={`disc-sn-${n.replace(/\s+/g, "-")}`}
                  onClick={() => update({ spaceName: n })}
                  className={`h-11 rounded-xl border text-sm transition ${
                    profile.spaceName === n
                      ? "bg-[color:var(--sauge)] text-[color:var(--bg-deep)] border-[color:var(--sauge)]"
                      : "border-[color:var(--border)] bg-[#142235]/60 text-[color:var(--ivoire)]"
                  }`}
                >{n}</button>
              ))}
            </div>
            <input
              data-testid="disc-space-name"
              value={profile.spaceName}
              onChange={(e) => update({ spaceName: e.target.value })}
              className="w-full h-12 px-4 rounded-xl bg-[#142235]/60 border border-[color:var(--border)] text-[color:var(--ivoire)]"
              placeholder="Ou saisir un nom personnalisé"
            />
            <button data-testid="disc-skip" onClick={nextStep} className="mt-3 text-xs text-[color:var(--ivoire-muted)] underline underline-offset-4">
              Passer cette question
            </button>
          </StepBlock>
        )}

        {currentKey === "summary" && (
          <div data-testid="disc-summary">
            <h2 className="font-serif text-2xl text-[color:var(--ivoire)]">Votre synthèse</h2>
            <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">Vous pourrez toujours modifier ces choix depuis Mon espace.</p>
            <div className="mt-6 space-y-2 text-sm">
              <SumRow label="Nom">{profile.displayName || "—"}</SumRow>
              <SumRow label="Pour">{profile.audience === "adult" ? "Moi" : profile.audience === "baby" ? "Bébé" : "Moi et bébé"}</SumRow>
              <SumRow label="Besoin prioritaire">{NEEDS.find(n => n.id === profile.priorityNeed)?.label || "—"}</SumRow>
              <SumRow label="Durée">{profile.duration} min</SumRow>
              <SumRow label="Espace">{profile.spaceName || "—"}</SumRow>
            </div>
          </div>
        )}
      </div>

      <div className="mt-10">
        {currentKey !== "summary" ? (
          <button
            data-testid="disc-next"
            onClick={nextStep}
            className="w-full h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium flex items-center justify-center gap-2"
          >
            Continuer <ChevronRight size={16} />
          </button>
        ) : (
          <button
            data-testid="disc-finish"
            onClick={finish}
            className="w-full h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium flex items-center justify-center gap-2"
          >
            <Check size={16} /> Commencer mon moment
          </button>
        )}
      </div>
    </div>
  );
}

function StepBlock({ title, hint, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-[color:var(--ivoire)] leading-snug">{title}</h2>
      {hint && <p className="text-xs text-[color:var(--ivoire-muted)] mt-2">{hint}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function RadioList({ options, value, onChange, testidPrefix }) {
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <button
          key={o.id}
          data-testid={`${testidPrefix}-${o.id}`}
          onClick={() => onChange(o.id)}
          className={`w-full text-left rounded-xl border px-4 py-3 transition ${
            value === o.id ? "border-[color:var(--sauge)] bg-[color:var(--sauge)]/15" : "border-[color:var(--border)] bg-[#142235]/60"
          }`}
        >
          <span className="text-[color:var(--ivoire)]">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

function MultiList({ options, value, onChange, testidPrefix }) {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <button
          key={o.id}
          data-testid={`${testidPrefix}-${o.id}`}
          onClick={() => toggle(o.id)}
          className={`w-full text-left rounded-xl border px-4 py-3 transition flex items-center justify-between ${
            value.includes(o.id) ? "border-[color:var(--sauge)] bg-[color:var(--sauge)]/15" : "border-[color:var(--border)] bg-[#142235]/60"
          }`}
        >
          <span className="text-[color:var(--ivoire)]">{o.label}</span>
          {value.includes(o.id) && <Check size={16} className="text-[color:var(--sauge-light)]" />}
        </button>
      ))}
    </div>
  );
}

function SumRow({ label, children }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[color:var(--border)] bg-[#142235]/50 px-4 py-3">
      <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--ivoire-subtle)]">{label}</span>
      <span className="text-[color:var(--ivoire)] text-sm text-right">{children}</span>
    </div>
  );
}
